import _get from 'lodash/get';
import _set from 'lodash/set';
import _throttle from 'lodash/throttle';
import store from '@/store';
import {
  FatalWebErrorException,
  APIException,
} from '@/lib/exceptions';
import { hasErrors, getErrors, windowOpen } from '@/lib/utils';
import { pruneSalesPersons } from '@/lib/serializerUtils';


const requestConfig = (config) => {
  if (!config.params) config.params = {};
  if (store.getters['auth/token']) config.headers.Authorization = `Bearer ${store.getters['auth/token']}`;
  if (config.data && config.data.append) {
    if (store.getters['auth/loggedInSiteId']) {
      config.data.append('requestsiteid', store.getters['auth/loggedInSiteId']);
    }
  }
  config.headers['Loft-Version'] = process.env.VUE_APP_LOFT_VER;
  config.headers['Loft-App'] = 'vue';
  config.headers['Loft-Auto-UI-Labels'] = 'true';
  return config;
};


const requestError = (error) => Promise.reject(error);


const updateToken = _throttle((response) => {
  store.dispatch('auth/updateToken', { token: response.headers['x-auth-token'], token_expiry: response.headers['x-auth-expiry'] });
}, 5000, { trailing: false, leading: true });


const responseError = (error) => {
  if (error.response &&
      error.response.status >= 400 &&
      // errors from LoftNode are already handled with growl:
      !(_get(error, 'request.responseURL') || '').startsWith('https://local.retailarchitects.com:8900')) {
    const errorMessage = `Loft server error (${error.response.status}). Please reload your browser and contact your system administrator if the issue persists.`;
    store.dispatch('notify/toggleFatalErrorModal', { errorMessage, bool: true });
    throw new FatalWebErrorException();
  }
};


const authError = (response) => {
  let text = 'Please login again to reauthenticate';
  // Make engineer aware authError normally would transition
  if (process.env.NODE_ENV === 'development') {
    text += ' (dev mode - preventing transition to login page)';
  }
  store.dispatch('notify/push', {
    title: 'Authentication Expired',
    text,
    ignoreDuplicates: true,
  });
  if (process.env.NODE_ENV !== 'development') {
    store.dispatch('auth/logout');
  }
  return response;
};


// eslint-disable-next-line max-lines-per-function
const responseConfig = (response) => {
  const silentResponse = _get(response, 'config.silentResponse', false);
  if (_get(response, 'headers["x-auth-token"]')) {
    updateToken(response);
  }
  if (response.data === null || _get(response.data, 'response', null) === null) {
    return response;
  }
  const responseObject = response.data.response;
  if (responseObject.status === '401') {
    return authError(response);
  }
  if (responseObject.fatal_error) {
    const debugLink = responseObject.debug_link || '';
    const err_msg = store.getters['translations/error'](responseObject.err_msg);
    store.dispatch('notify/toggleFatalErrorModal', { errorMessage: err_msg, debugLink, bool: true });
    throw new FatalWebErrorException(err_msg);
  }
  else {
    // if the server told us to clear all local cache, do it now!
    // but do not return, so that end user can see all notifications on the screen
    if (responseObject.clear_cache === true) {
      store.dispatch('meta/clearClientSetupData');
      // NOTE: Clear via native IndexedDB API, localForage.clear() is not working (https://github.com/localForage/localForage/issues/946)
      indexedDB.deleteDatabase('loft-ui-local');
    }
    const notification = responseObject.notification ? store.getters['translations/error'](responseObject.notification) : '';
    const notification_msg = responseObject.notification_msg ? store.getters['translations/error'](responseObject.notification_msg) : '';
    const notifyDuration = responseObject.notification_fixed ? { duration: -1 } : {};
    const errors = responseObject.results.reduce((acc, curr) => {
      return acc || hasErrors(curr);
    }, false);
    if ((errors || responseObject.errors) && !silentResponse) {
      if (responseObject.err_msg) {
        store.dispatch('notify/push', {
          title: responseObject.err_msg,
          ...notifyDuration,
        });
        return response;
      }
      if (response.config.url.endsWith('/get_updates')) {
        const _errors = getErrors(responseObject.results[0]);
        if (_errors.length && !responseObject.notification_msg) {
          for (const error of _errors) {
            const errorMessage = store.getters['translations/error'](error);
            store.dispatch('notify/push', {
              title: errorMessage,
              ...notifyDuration,
            });
          }
          return response;
        }
      }
      const exception = new APIException(notification, notification_msg, notifyDuration);
      store.dispatch('notify/push', exception.notifyParams);
    }
    else if (responseObject && responseObject.notification && !silentResponse) {
      store.dispatch('notify/push', {
        title: notification,
        text: notification_msg,
        type: responseObject.warnings ? 'warning' : 'success',
        ...notifyDuration,
      });
    }
    if (responseObject && responseObject.asyncrequests) {
      // handle `asyncrequests`
      store.dispatch('auth/processAsyncRequests', responseObject);
    }
  }
  if (responseObject.new_browser_window) {
    const results = responseObject.results;
    if (results) {
      for (const { url } of results) {
        if (url) windowOpen(url, responseObject.new_browser_window_target);
      }
    }
  }
  if (response.config.url.includes('customerorder')) {
    const salespersonKeys = ['salesperson1', 'salesperson2'];
    const customerOrder = _get(response, 'data.response.results[0]');
    if (customerOrder && customerOrder.orders && customerOrder.orders.length) {
      for (let index = 0; index < customerOrder.orders.length; index += 1) {
        const order = customerOrder.orders[index];
        const restrictedAttrs = order.restricted_attributes;
        for (let i = 0; i < salespersonKeys.length; i += 1) {
          const salespersonKey = salespersonKeys[i];
          if (restrictedAttrs && restrictedAttrs[salespersonKey]) {
            // If salesperson fields have restricted_attributes, then copy over to alias.
            if (!restrictedAttrs[`${salespersonKey}alias`]) restrictedAttrs[`${salespersonKey}alias`] = [];
            restrictedAttrs[`${salespersonKey}alias`].push(restrictedAttrs[salespersonKey]);
          }
        }
      }
      _set(response, 'data.response.results[0]', pruneSalesPersons(customerOrder));
    }
  }
  return response;
};


export {
  requestConfig,
  requestError,
  responseConfig,
  responseError,
  authError,
};
